/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */

import {
  Group,
  Schedule,
  Today,
} from '@mui/icons-material';
import AccessTime from '@mui/icons-material/AccessTime';
import EventRepeat from '@mui/icons-material/EventRepeat';
import { useColorScheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import RRuleGenerator from 'react-rrule-generator';
import { rrulestr } from 'rrule';
import { grey } from '../../../../../utils/brandColors';
import {
  fromPickerValue,
  toMomentValue,
} from '../../../../../utils/dateHelper';
import { getBarnUsersForAssignmentString } from '../../../../../utils/scheduleHelper';
import BarnUsersAutocomplete from '../../../../Autocompletes/BarnUsers';
import TimeSelect from '../../../../TimeSelect';
import ParticipantListComponent from '../../../ParticipantList';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const getDateTime = (date) => (date
  ? new Date(date).getTime()
  : null);

const isExistingRepeatingSchedule = (schedule) => {
  if (!schedule || !schedule.id) return false;
  const repeatSchedule = schedule.parentSchedule || schedule;
  if (!repeatSchedule || !repeatSchedule.id) return false;
  if (!repeatSchedule.endDate) return true;
  return getDateTime(repeatSchedule.startDate) !== getDateTime(repeatSchedule.endDate);
};

const SchedulingSection = ({
  assignment,
  setAssignment,
  validation,
  schedule,
  event,
  startDate,
  endDate,
  eventTemplate,
  duration,
  rrule,
  setStartDate,
  setEndDate,
  setRrule,
  setDuration,
  disabledForBeingInPast,
  timezoneWarning,
  open,
  stopRepeating,
  onStopRepeating,
  repeatEndDate,
}) => {
  const { mode } = useColorScheme();

  const [openRrule, setOpenRrule] = React.useState(false);
  const [openTimezoneWarning, setOpenTimezoneWarning] = React.useState(false);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  const repeatScheduleToUse = (schedule && schedule.parentSchedule) || schedule;
  const scheduleStartDate = new Date((repeatScheduleToUse && repeatScheduleToUse.startDate) || startDate);
  const isSingleOccurrence = !isExistingRepeatingSchedule(schedule)
    && endDate !== null
    && startDate.getTime() === endDate.getTime();

  React.useEffect(() => {
    if (openRrule) {
      setOpenRrule(false);
    }
    setOpenTimezoneWarning(false);
  }, [schedule]);

  if (!eventTemplate) return <div />;

  if (stopRepeating) {
    return (
      <Paper
        sx={{
          m: 1,
          p: 2,
        }}
      >
        <Typography>Repeating will end after {Moment(repeatEndDate).format('ll')}.</Typography>
        <Typography variant="body2">This occurrence and past events will remain. Later occurrences will be removed when you save.</Typography>
        <Link component="button" onClick={() => onStopRepeating(false)} sx={{ mt: 1 }}>Keep Repeating</Link>
      </Paper>
    );
  }

  const listItemSx = {
    px: 1,
    py: 0.75,
  };
  const listItemIconSx = {
    minWidth: 32,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
      height: '1.25rem',
      width: '1.25rem',
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing={1} sx={{ m: 1 }}>
        <Paper sx={{ borderRadius: 3 }}>
          <List disablePadding>
            <ListItem dense sx={listItemSx}>
              <ListItemIcon sx={listItemIconSx}><AccessTime /></ListItemIcon>
              <ListItemText>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid size={12}>
                    <DatePicker
                      label={(event || isSingleOccurrence)
                        ? 'Date'
                        : 'Start Date'}
                      value={toMomentValue(startDate)}
                      disabled={disabledForBeingInPast}
                      onChange={(newValue) => {
                        const newDateValue = fromPickerValue(newValue);
                        if (newDateValue) {
                          if (isSingleOccurrence) {
                            setEndDate(fromPickerValue(newValue));
                          }
                          setStartDate(fromPickerValue(newValue));
                        }
                      }}
                      slotProps={{
                        textField: {
                          disabled: disabledForBeingInPast,
                          helperText: validation.startDate,
                          error: !!validation.startDate,
                          size: 'small',
                          sx: {
                            width: '100%',
                            textWrap: 'auto',
                          },
                          required: true,
                        },
                      }}
                    />
                  </Grid>
                  <Grid>
                    <TimeSelect
                      label="Start Time"
                      value={startDate}
                      onChange={(e) => {
                        const newValue = new Date(e.target.value);
                        if (isSingleOccurrence) {
                          setEndDate(newValue);
                        }
                        setStartDate(newValue);
                      }}
                      sx={{
                        minWidth: 120,
                        mr: 1,
                      }}
                      disabled={disabledForBeingInPast}
                    />
                    <TimeSelect
                      label="End Time"
                      value={new Date(startDate.getTime() + duration)}
                      onChange={(e) => {
                        const newValue = new Date(e.target.value);
                        if (newValue.getTime() - startDate.getTime() !== 0) {
                          setDuration(newValue.getTime() - startDate.getTime());
                        }
                      }}
                      sx={{ minWidth: 120 }}
                      disabled={disabledForBeingInPast}
                    />
                  </Grid>
                  {timezoneWarning && (
                    <Grid>
                      <IconButton onClick={() => setOpenTimezoneWarning(!openTimezoneWarning)}>
                        <Schedule color="warning" />
                      </IconButton>
                    </Grid>
                  )}
                  {timezoneWarning && openTimezoneWarning && (
                    <Grid>
                      <Collapse in={openTimezoneWarning} timeout="auto" unmountOnExit>
                        {timezoneWarning}
                      </Collapse>
                    </Grid>
                  )}
                </Grid>
              </ListItemText>
            </ListItem>
          </List>
        </Paper>
        <Paper
          sx={{ borderRadius: 3 }}
        >
          <List disablePadding>
            <ListItem
              disabled={disabledForBeingInPast}
              dense
              sx={listItemSx}
            >
              <ListItemIcon sx={listItemIconSx}><EventRepeat /></ListItemIcon>
              <ListItemText>
                {isSingleOccurrence
                  ? (
                    <Tooltip title="Select to repeat this scheduled task">
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        underline="hover"
                        onClick={() => {
                          setEndDate(Moment(scheduleStartDate).add(7, 'days').toDate());
                          setRrule('RRULE:FREQ=DAILY;INTERVAL=1');
                          setOpenRrule(true);
                        }}
                      >
                        Repeat Event
                      </Link>
                    </Tooltip>
                  )
                  : (
                    <Typography color="primary" component="span" variant="subtitle1" sx={{ textWrap: 'auto' }}>
                      {`${capitalize(rrulestr(rrule).toText())} ${endDate
                        ? `ending ${Moment(endDate).format('ll')}`
                        : ''}`}
                    </Typography>
                  )}
                {!isSingleOccurrence && (
                  <Box sx={{ mt: 1 }}>
                    <DatePicker
                      label="End Date"
                      value={toMomentValue(endDate)}
                      minDate={toMomentValue(startDate)}
                      onChange={(newValue) => {
                        setEndDate(fromPickerValue(newValue));
                      }}
                      onClick={(e) => { e.stopPropagation(); }}
                      slotProps={{
                        textField: {
                          onClick: (e) => { e.stopPropagation(); },
                          helperText: validation.endDate,
                          error: !!validation.endDate,
                          disabled: !endDate,
                          size: 'small',
                          sx: {
                            maxWidth: 200,
                            mr: 1,
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </ListItemText>
              {!isSingleOccurrence && !disabledForBeingInPast
                && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip title="Remove repeating of this task">
                      <Link
                        component="button"
                        variant="body2"
                        color="secondary"
                        sx={{ ml: 1 }}
                        underline="hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (isExistingRepeatingSchedule(schedule)) {
                            onStopRepeating(true);
                          } else {
                            setEndDate(startDate);
                          }
                          setOpenRrule(false);
                        }}
                      >
                        Remove Repeat
                      </Link>
                    </Tooltip>
                  </Box>
                )}
            </ListItem>
            <Collapse in={openRrule} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                }}
              >
                <RRuleGenerator
                  onChange={(newRrule) => setRrule(newRrule)}
                  value={rrule}
                  config={{
                    repeat: ['Daily', 'Monthly', 'Weekly', 'Yearly'],
                    weekStartsOnSunday: true,
                    hideStart: true,
                    hideEnd: true,
                  }}
                />
              </Box>
            </Collapse>
          </List>
        </Paper>

        <Paper
          sx={{
            borderRadius: 3,
            p: 2,
            backgroundColor: grey[mode === 'dark'
              ? 700
              : 100],
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Today />
              <Box>
                <Typography>Calendar Invitation</Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    fontWeight: 300,
                    textWrap: 'auto',
                  }}
                >
                  Participants are emailed an invite. Questri records accept and decline responses.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Group sx={{ mt: '4px' }} />
              <Box sx={{ flex: 1 }}>
                <BarnUsersAutocomplete
                  label="Participants"
                  id="barn-users-select-autocomplete-schedule-assignment"
                  multiple
                  validation={validation.assignment}
                  value={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                  onSelect={(e, newBarnUsers) => {
                    setAssignment(newBarnUsers.map((bu) => (bu.id
                      ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                      : `@[${bu.userFullName || bu}](${null})`)).join(''));
                  }}
                  limitTags={(schedule && schedule.id)
                    ? 2
                    : 5}
                  disabled={disabledForBeingInPast}
                />
                {schedule && schedule.id && open && (
                  <Box sx={{ mt: 1 }}>
                    <ParticipantListComponent
                      participants={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
                      schedule={schedule}
                    />
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
};

SchedulingSection.propTypes = {
  validation: PropTypes.object,
  schedule: PropTypes.object,
  event: PropTypes.object,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  eventTemplate: PropTypes.object,
  duration: PropTypes.number,
  rrule: PropTypes.string,
  setStartDate: PropTypes.func.isRequired,
  setEndDate: PropTypes.func.isRequired,
  setRrule: PropTypes.func.isRequired,
  setDuration: PropTypes.func.isRequired,
  disabledForBeingInPast: PropTypes.bool,
  timezoneWarning: PropTypes.string,
  assignment: PropTypes.string,
  setAssignment: PropTypes.func.isRequired,
  open: PropTypes.bool,
  stopRepeating: PropTypes.bool,
  onStopRepeating: PropTypes.func,
  repeatEndDate: PropTypes.instanceOf(Date),
};

SchedulingSection.defaultProps = {
  validation: {},
  schedule: null,
  event: null,
  startDate: new Date(),
  endDate: new Date(),
  eventTemplate: null,
  duration: 0,
  rrule: 'RRULE:FREQ=DAILY;INTERVAL=1',
  disabledForBeingInPast: false,
  timezoneWarning: null,
  assignment: '',
  open: false,
  stopRepeating: false,
  onStopRepeating: () => {},
  repeatEndDate: null,
};

export default SchedulingSection;
